import React, { useState, useEffect } from 'react';
import styles from '../furnishingContainer.module.scss';
import { PortfolioProject, PortfolioRoom, PortfolioReview } from './portfolio.types';
import PortfolioForm from './PortfolioForm';
import PortfolioRoomForm from './PortfolioRoomForm';
import PortfolioReviewForm from './PortfolioReviewForm';
import PortfolioList from './PortfolioList';
import {
  getAllPortfolios,
  getAllPortfolioRooms,
  getAllPortfolioReviews,
  deletePortfolio,
  updatePortfolio,
  updatePortfolioImage,
  PortfolioResponse,
  PortfolioRoomResponse,
  PortfolioReviewResponse
} from '../furnishing.service';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';

interface PortfolioTabContainerProps {
  packages?: { value: string; label: string }[];
  models?: { value: string; label: string }[] | undefined;
}

export const PortfolioTabContainer: React.FC<PortfolioTabContainerProps> = ({
  packages = [],
  models = []
}) => {
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    const fetchPortfolios = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const [portfoliosResult, roomsResult, reviewsResult] = await Promise.all([
          getAllPortfolios(),
          getAllPortfolioRooms(),
          getAllPortfolioReviews()
        ]);

        if (isErrorInfo(portfoliosResult)) {
          setFetchError(portfoliosResult.errorMessage || 'Failed to load portfolios.');
          return;
        }

        // Create a map of portfolio rooms grouped by portfolioId
        const roomsByPortfolioId: Record<string, PortfolioRoom[]> = {};
        if (!isErrorInfo(roomsResult)) {
          roomsResult.forEach((room: PortfolioRoomResponse) => {
            if (!roomsByPortfolioId[room.portfolioId]) {
              roomsByPortfolioId[room.portfolioId] = [];
            }
            roomsByPortfolioId[room.portfolioId].push({
              id: room.id,
              name: room.name,
              images: room.images,
              order: room.order
            });
          });
        }

        // Create a map of portfolio reviews by portfolioId (single review per portfolio)
        const reviewsByPortfolioId: Record<string, PortfolioReview> = {};
        if (!isErrorInfo(reviewsResult)) {
          reviewsResult.forEach((review: PortfolioReviewResponse) => {
            reviewsByPortfolioId[review.portfolioId] = {
              id: review.id,
              portfolioId: review.portfolioId,
              name: review.name,
              description: review.description
            };
          });
        }

        const mappedProjects: PortfolioProject[] = portfoliosResult.map(
          (item: PortfolioResponse) => ({
            id: item.id,
            projectName: item.name,
            projectNameAr: item.nameAr,
            description: item.description,
            descriptionAr: item.descriptionAr,
            location: item.location,
            priceMin: item.minPrice,
            priceMax: item.maxPrice,
            homeType: item.homeType,
            packageId: item.packageId,
            modelId: item.modelId,
            otherModel: item.otherModel,
            numberOfBedrooms: item.numberOfBedrooms,
            timeline: item.timeline,
            year: item.year,
            overviewImage: item.image,
            rooms: roomsByPortfolioId[item.id] || [],
            review: reviewsByPortfolioId[item.id],
            isPublished: item.isPublished ?? false,
            order: item.order
          })
        );
        setProjects(mappedProjects);
      } catch (err) {
        setFetchError('An unexpected error occurred while loading portfolios.');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolios();
  }, []);

  const handleProjectAdded = (project: PortfolioProject) => {
    setProjects((prev) => [...prev, project]);
  };

  const handleRoomAdded = (portfolioId: string, room: PortfolioRoom) => {
    setProjects((prev) =>
      prev.map((project) =>
        project.id === portfolioId ? { ...project, rooms: [...project.rooms, room] } : project
      )
    );
  };

  const handleReviewAdded = (portfolioId: string, review: PortfolioReview) => {
    setProjects((prev) =>
      prev.map((project) => (project.id === portfolioId ? { ...project, review } : project))
    );
  };

  const handleProjectUpdated = async () => {
    setFetchError('');
    try {
      const [portfoliosResult, roomsResult, reviewsResult] = await Promise.all([
        getAllPortfolios(),
        getAllPortfolioRooms(),
        getAllPortfolioReviews()
      ]);

      if (isErrorInfo(portfoliosResult)) {
        setFetchError(portfoliosResult.errorMessage || 'Failed to load portfolios.');
        return;
      }

      const roomsByPortfolioId: Record<string, PortfolioRoom[]> = {};
      if (!isErrorInfo(roomsResult)) {
        roomsResult.forEach((room: PortfolioRoomResponse) => {
          if (!roomsByPortfolioId[room.portfolioId]) {
            roomsByPortfolioId[room.portfolioId] = [];
          }
          roomsByPortfolioId[room.portfolioId].push({
            id: room.id,
            name: room.name,
            images: room.images,
            order: room.order
          });
        });
      }

      const reviewsByPortfolioId: Record<string, PortfolioReview> = {};
      if (!isErrorInfo(reviewsResult)) {
        reviewsResult.forEach((review: PortfolioReviewResponse) => {
          reviewsByPortfolioId[review.portfolioId] = {
            id: review.id,
            portfolioId: review.portfolioId,
            name: review.name,
            description: review.description
          };
        });
      }

      const mappedProjects: PortfolioProject[] = portfoliosResult.map(
        (item: PortfolioResponse) => ({
          id: item.id,
          projectName: item.name,
          projectNameAr: item.nameAr,
          description: item.description,
          descriptionAr: item.descriptionAr,
          location: item.location,
          priceMin: item.minPrice,
          priceMax: item.maxPrice,
          homeType: item.homeType,
          packageId: item.packageId,
          modelId: item.modelId,
          otherModel: item.otherModel,
          numberOfBedrooms: item.numberOfBedrooms,
          timeline: item.timeline,
          year: item.year,
          overviewImage: item.image,
          rooms: roomsByPortfolioId[item.id] || [],
          review: reviewsByPortfolioId[item.id],
          isPublished: item.isPublished ?? false,
          order: item.order
        })
      );
      setProjects(mappedProjects);
    } catch (err) {
      setFetchError('An unexpected error occurred while loading portfolios.');
    }
  };

  const handleDeleteProject = async (id: string) => {
    try {
      const result = await deletePortfolio(id);
      if (!isErrorInfo(result)) {
        setProjects((prev) => prev.filter((p) => p.id !== id));
      } else {
        setFetchError(result.errorMessage || 'Failed to delete portfolio.');
      }
    } catch (err) {
      setFetchError('An unexpected error occurred while deleting the portfolio.');
    }
  };

  const handleEditProject = async (
    id: string,
    data: Partial<PortfolioProject>,
    newImage?: File
  ) => {
    try {
      // Update main info using PUT /portfolio/{id} with set/unset
      const updateData = {
        name: data.projectName,
        nameAr: data.projectNameAr,
        location: data.location,
        year: data.year,
        homeType: data.homeType,
        packageId: data.packageId,
        numberOfBedrooms: data.numberOfBedrooms,
        minPrice: data.priceMin,
        maxPrice: data.priceMax,
        description: data.description,
        descriptionAr: data.descriptionAr,
        isPublished: data?.isPublished,
        modelId: data?.modelId,
        otherModel: data?.otherModel,
        order: data?.order,
        unsetModelId: data?.otherModel ? true : undefined
      };

      const updateResult = await updatePortfolio(id, updateData);
      if (isErrorInfo(updateResult)) {
        setFetchError(updateResult.errorMessage || 'Failed to update portfolio.');
        throw new Error(updateResult.errorMessage);
      }

      if (newImage) {
        const imageResult = await updatePortfolioImage(id, newImage);
        if (isErrorInfo(imageResult)) {
          setFetchError(imageResult.errorMessage || 'Failed to update portfolio image.');
          throw new Error(imageResult.errorMessage);
        }
      }

      setProjects((prev) =>
        prev.map((project) =>
          project.id === id
            ? {
                ...project,
                projectName: data.projectName || project.projectName,
                projectNameAr: data.projectNameAr || project.projectNameAr,
                description: data.description || project.description,
                descriptionAr: data.descriptionAr || project.descriptionAr,
                location: data.location || project.location,
                priceMin: data.priceMin ?? project.priceMin,
                priceMax: data.priceMax ?? project.priceMax,
                homeType: data.homeType || project.homeType,
                packageId: data.packageId || project.packageId,
                numberOfBedrooms: data.numberOfBedrooms ?? project.numberOfBedrooms,
                timeline: data.timeline ?? project.timeline,
                year: data.year ?? project.year,
                overviewImage: data.overviewImage || project.overviewImage,
                modelId: data.otherModel ? undefined : data.modelId || project.modelId,
                otherModel: data.otherModel || project.otherModel,
                order: data.order ?? project.order
              }
            : project
        )
      );
    } catch (err) {
      setFetchError('An unexpected error occurred while updating the portfolio.');
      throw err;
    }
  };

  const defaultPackages = packages.length > 0 ? packages : [];
  const defaultModels = models.length > 0 ? models : [];

  return (
    <div className={styles['furnishing-management__portfolio']}>
      <PortfolioForm
        packages={defaultPackages}
        models={defaultModels}
        onProjectAdded={handleProjectAdded}
      />

      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          Loading portfolios...
        </div>
      )}

      {fetchError && (
        <div
          style={{
            padding: '1rem',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '8px',
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '500'
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚠️</span> {fetchError}
        </div>
      )}

      {!loading && projects?.length > 0 && (
        <PortfolioRoomForm portfolios={projects} onRoomAdded={handleRoomAdded} />
      )}

      {!loading && projects?.length > 0 && (
        <PortfolioReviewForm portfolios={projects} onReviewAdded={handleReviewAdded} />
      )}

      {!loading && (
        <PortfolioList
          projects={projects}
          packages={defaultPackages}
          models={defaultModels}
          onDelete={handleDeleteProject}
          onEdit={handleEditProject}
          onProjectUpdated={handleProjectUpdated}
        />
      )}
    </div>
  );
};
